<template>
  <div ref="container" class="lottie-player" :style="{ width: size, height: size }" />
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import lottie, { type AnimationItem } from 'lottie-web'
import usePlatform from 'src/composables/usePlatform'

const { isCapacitor } = usePlatform()

const props = withDefaults(
  defineProps<{
    /** URL/path to the Lottie JSON (e.g. a file under /public). */
    src: string
    /** CSS size for the square container. */
    size?: string
    loop?: boolean
    autoplay?: boolean
    /** Fill the container (crop to cover) instead of fitting inside it (letterbox). */
    cover?: boolean
  }>(),
  { size: '64px', loop: true, autoplay: true, cover: false },
)

// Emitted when the JSON at `src` can't be loaded (e.g. file doesn't exist) — lets
// the caller fall back to a static image. Fired on lottie-web's 'data_failed'.
const emit = defineEmits<{ (e: 'error'): void }>()

const container = ref<HTMLElement | null>(null)
let anim: AnimationItem | null = null

function destroy() {
  anim?.destroy()
  anim = null
}

function load() {
  destroy()
  if (!container.value) return
  anim = lottie.loadAnimation({
    container: container.value,
    // Native: the canvas renderer is far lighter on Android WebView, and we
    // don't loop forever (continuous repaint drains CPU at idle) — play once.
    // Web keeps the crisp SVG renderer and the original loop behaviour.
    renderer: isCapacitor ? 'canvas' : 'svg',
    loop: isCapacitor ? false : props.loop,
    autoplay: props.autoplay,
    path: props.src,
    // 'slice' fills the container (crops overflow); 'meet' fits inside (letterboxes).
    rendererSettings: { preserveAspectRatio: props.cover ? 'xMidYMid slice' : 'xMidYMid meet' },
  })
  anim.addEventListener('data_failed', () => emit('error'))
}

onMounted(load)
watch(() => props.src, load)
onBeforeUnmount(destroy)
</script>

<style scoped>
.lottie-player {
  display: block;
}
</style>
